/*
 * File: StudentHeader.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';

import type { CoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod';
import type { GetStudentResponse } from 'api/hooks/private/users/students/GetStudent';

import RequireRole from 'components/RequireRole';

import { useDriveOverviewDialogContext } from '../../context';
import StudentActionsMenu from '../StudentActionsMenu';

interface StudentHeaderProps {
  /**
   * Student user data
   */
  student: GetStudentResponse | null | undefined;

  /**
   * Display text for drive number (e.g., "#3" or "Canceled")
   */
  driveLabel: string;

  /**
   * Type of enrollment
   */
  enrollmentType: CoursePeriod['period_enrollments'][0]['enrollment_type'];

  /**
   * Course period ID for the drive
   */
  coursePeriodId: string;
}

/**
 * Header section for StudentSection component
 * Displays student name, drive number, enrollment type chip, and optional view profile button
 */
export default function StudentHeader({ student, driveLabel, enrollmentType, coursePeriodId }: StudentHeaderProps) {
  const { onClose } = useDriveOverviewDialogContext();
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6" fontWeight="medium">
          {student?.first_name} {student?.middle_name ? student?.middle_name + ' ' : ''}
          {student?.last_name} ({driveLabel})
        </Typography>
        <RequireRole role="ADMIN">
          <Tooltip title="View student profile">
            <IconButton
              component={RouterLink}
              to={`admin/students/${student?.id}`}
              size="small"
              sx={{ color: 'primary.main' }}
              onClick={onClose}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </RequireRole>
        <RequireRole role={['ADMIN', 'INSTRUCTOR']}>
          <StudentActionsMenu
            studentId={student?.id ?? ''}
            coursePeriodId={coursePeriodId}
            studentName={`${student?.first_name} ${student?.last_name}`}
          />
        </RequireRole>
      </Box>

      {enrollmentType === 'observation_only' && (
        <Chip icon={<VisibilityIcon />} label="Observation Only" size="small" color="primary" variant="outlined" />
      )}
    </Box>
  );
}
